import * as React from 'react';

import { Input as ShadcnInput } from '@/hen-ui/shadcn/input';
import { Icon, type LucideIconName } from '@/hen-ui/atomics/Icon';
import { inputVariants, type InputVariantProps } from './input.variants';
import { cn } from '@/hen-ui/utils';

export interface InputProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnInput>,
    InputVariantProps {
  /**
   * Error state - when true, displays error styling and sets aria-invalid
   */
  error?: boolean;
  /**
   * Error message text - when provided, sets aria-describedby and aria-invalid
   */
  errorMessage?: string;
  /**
   * Icon name to display on the left side of the input (uses Icon atomic component)
   */
  icon?: LucideIconName;
  /**
   * Label text - when variant is 'two-line', this label will animate from placeholder to top position
   */
  label?: string;
}

/**
 * Input component following HEN Design System standards.
 *
 * A standardized text input field wrapping the native HTML input element
 * with HEN-specific variants and design tokens. All styling uses tokens
 * from tailwind.config.ts.
 *
 * @example
 * // Basic usage
 * <Input type="text" placeholder="Enter your name" />
 *
 * @example
 * // With label and id for accessibility
 * <label htmlFor="email-input">Email</label>
 * <Input id="email-input" type="email" placeholder="email@example.com" />
 *
 * @example
 * // With error state
 * <Input
 *   type="text"
 *   placeholder="Enter value"
 *   error
 *   errorMessage="This field is required"
 * />
 *
 * @example
 * // Disabled state
 * <Input type="text" placeholder="Disabled input" disabled />
 *
 * @example
 * // With icon (uses Icon atomic)
 * // <Input type="search" placeholder="Search..." icon="search" />
 *
 * @example
 * // Two-line label variant
 * <Input variant="two-line" label="Email Address" type="email" />
 */
export const Input = React.forwardRef<
  React.ElementRef<typeof ShadcnInput>,
  InputProps
>(
  (
    {
      className,
      variant,
      error,
      errorMessage,
      icon,
      label,
      id,
      value,
      defaultValue,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      ...props
    },
    ref,
  ) => {
    // Generate unique id if not provided (for aria-describedby)
    const inputId = React.useId();
    const finalId = id || inputId;
    const errorId = errorMessage ? `${finalId}-error` : undefined;
    const labelId = label ? `${finalId}-label` : undefined;

    // Track focus and value state for two-line label
    const [isFocused, setIsFocused] = React.useState(false);
    const [hasValue, setHasValue] = React.useState(
      Boolean(value || defaultValue),
    );

    // Determine if label should be floating (at top)
    const isFloating = variant === 'two-line' && (isFocused || hasValue);

    // Combine aria-describedby values
    const describedBy =
      [ariaDescribedBy, errorId].filter(Boolean).join(' ') || undefined;

    // Set aria-invalid based on error prop or errorMessage
    const isInvalid = error || Boolean(errorMessage);
    const finalAriaInvalid =
      ariaInvalid !== undefined ? ariaInvalid : isInvalid ? 'true' : undefined;

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      props.onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setHasValue(Boolean(e.target.value));
      props.onChange?.(e);
    };

    return (
      <>
        <div className='relative w-full'>
          {icon && (
            <div
              className='absolute left-4 top-1/2 -translate-y-1/2 z-10 pointer-events-none'
              aria-hidden='true'
            >
              <Icon icon={icon} className='text-subtext' />
            </div>
          )}
          {variant === 'two-line' && label && (
            <label
              id={labelId}
              htmlFor={finalId}
              className={cn(
                'absolute left-3 pointer-events-none',
                'transition-all duration-200 ease-in-out',
                'text-body font-body',
                isFloating
                  ? 'top-[6px] text-l2 leading-[14px]'
                  : 'top-1/2 -translate-y-1/2 text-b5 leading-[22px]',
                isInvalid && isFloating ? 'text-error-500' : '',
                isFloating && !isInvalid ? 'text-body' : 'text-subtext',
              )}
            >
              {label}
            </label>
          )}
          <ShadcnInput
            className={cn(
              inputVariants({ variant, error: isInvalid }),
              // icon && 'pl-10',
              className,
            )}
            ref={ref}
            id={finalId}
            value={value}
            defaultValue={defaultValue}
            aria-invalid={finalAriaInvalid}
            aria-describedby={describedBy}
            aria-labelledby={
              variant === 'two-line' && label ? labelId : undefined
            }
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            {...props}
          />
        </div>
        {errorMessage && (
          <div
            id={errorId}
            className='mt-2 text-sm text-error-500'
            role='alert'
            aria-live='polite'
          >
            {errorMessage}
          </div>
        )}
      </>
    );
  },
);

Input.displayName = 'Input';
